import { useCallback, useContext } from 'react';

import {
	EDIT_PAGE_EMBED,
	EMBED_VIEW,
	EMBEDDED_MULTIPLE_SUBCALENDAR,
	SPACE_EMBEDDED_SUBCALENDAR,
} from '@confluence/named-routes/entry-points/routes.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';

const ADD_CONNECTOR_PARAM = 'add-connector';
const SOURCE_PARAM = 'source';

/**
 * Embed/standalone routes where the deep link must never act, even if the param
 * is present. Admin needs no entry here: admin routes are not part of the SPA
 * `routeComponents`, so `GlobalComponents` (and therefore this hook) never
 * renders on them.
 */
const EXCLUDED_ROUTE_NAMES = new Set<string>([
	EDIT_PAGE_EMBED.name,
	EMBED_VIEW.name,
	EMBEDDED_MULTIPLE_SUBCALENDAR.name,
	SPACE_EMBEDDED_SUBCALENDAR.name,
]);

/**
 * Reactively detects the `?add-connector` deep link on any Confluence page.
 *
 * `isDeepLinkActive` is recomputed on every render from the router match query
 * (it is NOT frozen), so it reacts to the param being added live via
 * router-driven navigation / `setQueryParams` — not only on a full page load.
 * The query is parsed from the full `location.search` (not schema-filtered) and
 * is SSR-safe; no `window.location` snapshot is needed.
 *
 * Note: this reacts to router-driven URL changes. A bare `history.pushState`
 * that bypasses the router would not update `match.query` and so would not be
 * detected.
 *
 * `markHandled` strips the deep-link params via `history.replace`, preserving
 * all other params. The caller keeps the modal mounted independently of
 * `isDeepLinkActive`, so stripping the param cannot unmount/close it.
 */
export const useConnectorDeepLink = (): {
	isDeepLinkActive: boolean;
	markHandled: () => void;
} => {
	const { match, setQueryParams } = useContext(RoutesContext);

	const query = match?.query ?? {};
	const isExcludedRoute = match?.name ? EXCLUDED_ROUTE_NAMES.has(match.name) : false;
	const isDeepLinkActive = ADD_CONNECTOR_PARAM in query && !isExcludedRoute;

	const markHandled = useCallback(() => {
		// `setQueryParams` merges then deletes null/undefined keys, so this removes
		// our params and is a no-op for any that aren't present.
		setQueryParams({ [ADD_CONNECTOR_PARAM]: undefined, [SOURCE_PARAM]: undefined }, true);
	}, [setQueryParams]);

	return { isDeepLinkActive, markHandled };
};
